/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, {
	createContext,
	useCallback,
	useContext,
	useState,
	useMemo,
	type ReactNode,
} from 'react';
import { jiraHomeRoute } from '@jira/router-routes__jira-home-routes/src/jiraHomeRoute.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import { storageProvider } from './utils.tsx';

const STORAGE_KEY = 'back-button-url';

type NavigateBackContextType = {
	saveCurrentRoute: (currentUrl: string) => void;
	previousUrl: string | null /* use this instead of goBack() */;
};

const NavigateBackContext = createContext<NavigateBackContextType | undefined>(undefined);

export const useNavigateBack = (): NavigateBackContextType => {
	const context = useContext(NavigateBackContext);
	if (!context) {
		throw new Error('useNavigateBack must be used within a NavigateBackProvider');
	}
	return context;
};

/**
 * Uses session storage instead of relying on browser history - to track of the last non-Settings menu page, to go "Back" to. See usages of saveCurrentRoute() - when new route is saved.
 */
export function NavigateBackProvider({ children }: { children: ReactNode }): React.JSX.Element {
	const [projectKey] = usePathParam('projectKey');
	const defaultInitialUrl = projectKey ? `/projects/${projectKey}` : jiraHomeRoute.path;
	const [previousUrl, setPreviousUrl] = useState<string>(
		() => storageProvider.get(STORAGE_KEY) ?? defaultInitialUrl,
	);

	const saveCurrentRoute = useCallback((currentUrl: string) => {
		setPreviousUrl(currentUrl);
		// @browser-storage setter
		storageProvider.setStrictlyNecessaryItem(STORAGE_KEY, currentUrl);
	}, []);

	const value = useMemo(() => ({ saveCurrentRoute, previousUrl }), [saveCurrentRoute, previousUrl]);

	return <NavigateBackContext.Provider value={value}>{children}</NavigateBackContext.Provider>;
}
